/**
 * Implementación del repositorio de Notification usando HTTP
 */

import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '@env';
import { NotificationRepository } from '@domain/repositories/notification.repository';
import { Notification } from '@domain/models/notification.model';
import { NotificationDto, NotificationResponseDto } from '@infra/dto/notification.dto';
import { NotificationMapper } from '@infra/mappers/notification.mapper';

@Injectable({ providedIn: 'root' })
export class NotificationRepositoryImpl implements NotificationRepository {
  private http = inject(HttpClient);

  getQuoteNotifications(page: number, pageSize: number): Observable<Notification[]> {
    const url = `${environment.apiUrl}/v1/notifications/me`;
    const params = new HttpParams()
      .set('page', String(page))
      .set('pageSize', String(pageSize))
      .set('page_size', String(pageSize))
      .set('limit', String(pageSize));

    return this.http.get<NotificationResponseDto>(url, { params }).pipe(
      map(response => this.extractNotifications(response))
    );
  }

  getContractNotifications(page: number, pageSize: number): Observable<Notification[]> {
    const url = `${environment.apiUrl}/v1/contracts/notifications`;
    const params = new HttpParams()
      .set('page', String(page))
      .set('pageSize', String(pageSize))
      .set('page_size', String(pageSize))
      .set('limit', String(pageSize));

    return this.http.get<NotificationResponseDto>(url, { params }).pipe(
      map(response => this.extractNotifications(response))
    );
  }

  markAsRead(notificationId: string): Observable<void> {
    // Intentar primero con el endpoint de contratos
    const contractsUrl = `${environment.apiUrl}/v1/contracts/notifications/${encodeURIComponent(notificationId)}/read`;
    const quotesUrl = `${environment.apiUrl}/v1/notifications/${encodeURIComponent(notificationId)}/read`;

    // Intentar primero contratos, si falla intentar cotizaciones
    return this.http.put<void>(contractsUrl, {}).pipe(
      // Si falla, intentar con el endpoint de cotizaciones
      // Esto se manejará mejor con catchError en el servicio
    );
  }

  /**
   * Extrae notificaciones desde diferentes estructuras de respuesta
   */
  private extractNotifications(resp: NotificationResponseDto): Notification[] {
    if (!resp) return [];

    // Casos directos
    if (Array.isArray(resp)) {
      return NotificationMapper.toDomainList(resp as NotificationDto[]);
    }
    if (Array.isArray(resp.items)) {
      return NotificationMapper.toDomainList(resp.items);
    }
    if (Array.isArray(resp.notifications)) {
      return NotificationMapper.toDomainList(resp.notifications);
    }
    if (Array.isArray(resp.data)) {
      return NotificationMapper.toDomainList(resp.data as NotificationDto[]);
    }

    // Resp.data puede ser un wrapper con items/results/rows
    if (resp.data && typeof resp.data === 'object' && !Array.isArray(resp.data)) {
      if (Array.isArray(resp.data.items)) {
        return NotificationMapper.toDomainList(resp.data.items);
      }
      if (Array.isArray(resp.data.results)) {
        return NotificationMapper.toDomainList(resp.data.results);
      }
      if (Array.isArray(resp.data.rows)) {
        return NotificationMapper.toDomainList(resp.data.rows);
      }
      if (Array.isArray(resp.data.notifications)) {
        return NotificationMapper.toDomainList(resp.data.notifications);
      }
      if (Array.isArray(resp.data.data)) {
        return NotificationMapper.toDomainList(resp.data.data);
      }
    }

    // Intenta buscar en propiedades comunes anidadas
    const candidates = ['items', 'results', 'rows', 'notifications', 'data'];
    for (const key of candidates) {
      const value = (resp as any)[key];
      if (Array.isArray(value)) {
        return NotificationMapper.toDomainList(value as NotificationDto[]);
      }
      if (value && typeof value === 'object') {
        if (Array.isArray(value.data)) {
          return NotificationMapper.toDomainList(value.data as NotificationDto[]);
        }
        if (Array.isArray(value.items)) {
          return NotificationMapper.toDomainList(value.items);
        }
      }
    }

    return [];
  }
}

